import React, { memo, useEffect, useMemo, useRef } from 'react'
import { FlatList } from 'react-native'

import { publishEvent } from '@zet/analytics'
import { B2, Icon, Stack } from '@zet/design-system'
import Pressable from '@zet/design-system/pressable'
import { Categories } from '@zet/utils/Constants'
import { isCustomerFlow } from '@zet/utils/isCustomerFlow'

import { ICategoryId } from '../../api/brands/types'

const CATEGORY_ORDER = [
  Categories.CREDIT_CARDS,
  Categories.PERSONAL_LOANS,
  Categories.DIGITAL_GOLD,
  Categories.CIBIL_BUILDER_LOANS,
  Categories.BANK_ACCOUNTS,
  Categories.INSURANCE,
  Categories.FIXED_DEPOSIT,
  Categories.DEMAT_ACCOUNTS,
  Categories.BUSINESS_LOANS,
  Categories.OTHER_LOANS,
  Categories.SECURED_CREDIT_CARDS,
  Categories.TWO_WHEELER_LOANS,
  Categories.INSTANT_LOANS,
  Categories.GOLD_LOAN,
  Categories.PAY_LATER_EMI_CARDS,
  Categories.OTHER,
]
const CATEGORY_ICON_MAP = {
  15: 'CreditCardOutlined',
  9: 'MoneyBagOutlined',
  10: 'BusinessShopOutlined',
  12: 'GoldBarsOutlined',
}

export interface CategoryFilterList {
  categoryName: string
  categoryId: ICategoryId | number | string
  totalCount: number
}

const CategoryFilter = ({
  handlePress,
  data = [],
  selectedCategory,
}: {
  handlePress: (categoryId: string, activeIndex: number) => void
  data: Array<CategoryFilterList>
  selectedCategory: {
    categoryId: string | number
    categoryName: string
    totalCount: number
  }
}) => {
  const flatList = useRef<FlatList<CategoryFilterList>>(null)

  const categoryFilters = useMemo(
    () =>
      CATEGORY_ORDER.filter((categoryId) =>
        data?.find(
          (category) =>
            (String(category.categoryId) === String(categoryId) ||
              String(categoryId) === String(selectedCategory?.categoryId)) &&
            (isCustomerFlow() ? Boolean(category.totalCount) : true)
        )
      ).map(
        (categoryId) =>
          data.find(
            (category) => String(category.categoryId) === String(categoryId)
          ) || selectedCategory
      ),
    [data, selectedCategory]
  )

  useEffect(() => {
    if (flatList?.current && selectedCategory) {
      const index = categoryFilters.findIndex(
        (category) =>
          String(category.categoryId) === String(selectedCategory.categoryId)
      )
      if (index > -1) {
        flatList.current.scrollToIndex({ index, animated: true })
      }
    }
  }, [selectedCategory])

  return (
    <Stack>
      <FlatList
        horizontal={true}
        showsHorizontalScrollIndicator={false}
        ref={flatList}
        onScrollToIndexFailed={(info) => {
          const wait = new Promise((resolve) => setTimeout(resolve, 500))
          wait.then(() => {
            flatList.current?.scrollToIndex({
              index: info.index,
              animated: true,
            })
          })
        }}
        className="bg-primary-200"
        data={categoryFilters}
        keyExtractor={(item) => String(item.categoryId)}
        renderItem={({ item, index }) => {
          const { categoryId, categoryName } = item
          return (
            <Pressable
              className={`py-4/2 flex-row items-center gap-x-2 px-3 ${
                String(selectedCategory?.categoryId) === String(categoryId)
                  ? 'border-primary-50 border-b-2 '
                  : 'border-primary-200 border-b-2'
              } ${index === data.length - 1 ? ' mr-3' : ''}`}
              key={categoryId}
              onPress={() => {
                publishEvent('click_action', {
                  item_name: `other_products_web`,
                  product: categoryId,
                })
                handlePress(categoryId, index)
              }}
            >
              <Icon
                type={CATEGORY_ICON_MAP[categoryId] || 'CurrencyNoteMoney'}
                color={
                  String(selectedCategory?.categoryId) === String(categoryId)
                    ? 'white'
                    : '#C7C2C9'
                }
              />
              <B2
                className={`text-center ${
                  String(selectedCategory?.categoryId) === String(categoryId)
                    ? 'text-white'
                    : 'text-grays-60'
                }`}
              >
                {categoryName}
              </B2>
            </Pressable>
          )
        }}
      />
    </Stack>
  )
}

export default memo(CategoryFilter)
